<template>
  <div class="view-all-panel">
    <div class="panel-header">
      <button
        class="close-btn"
        aria-label="Close panel"
        @click="$emit('close')"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <h2 class="panel-title">
        {{ panelTitle }}
      </h2>
    </div>
    <div class="panel-body">
      <div
        v-if="loading"
        class="panel-loading"
      >
        Loading...
      </div>
      <div
        v-else-if="error"
        class="panel-error"
      >
        Error: {{ error.message }}
      </div>
      <template v-else-if="accounts.length > 0">
        <table class="panel-table">
          <thead>
            <tr>
              <th>Account</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(account, index) in visibleAccounts"
              :key="account.account_id || index"
              class="clickable"
              @click="handleAccountClickGuarded(account)"
            >
              <td>
                <div class="user-cell">
                  <div class="user-image">
                    <img
                      v-if="account.avatar"
                      :src="account.avatar"
                      :alt="account.name"
                    >
                    <div
                      v-else
                      class="image-placeholder"
                    >
                      {{ getInitials(account.name) }}
                    </div>
                  </div>
                  <span class="user-name">{{ account.name || 'Unknown' }}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <button
          v-if="hasMore"
          class="load-more-btn"
          @click="loadMore"
        >
          Load more
        </button>
      </template>
      <div
        v-else
        class="panel-empty"
      >
        No accounts found
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onUnmounted } from 'vue'
import { useDashboardKPIsData } from '../composables/useWidgetData.js'
import { getAvatarUrl } from '../utils/avatar.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'

defineEmits(['close'])

const CONVERSION_TYPES = {
  'dropin-member': {
    title: 'Drop-in \u2192 member conversions',
    dataKey: 'dropin_converted_accounts'
  },
  'tour-member': {
    title: 'Tour \u2192 member conversions',
    dataKey: 'tour_converted_accounts'
  },
  'inquiry-member': {
    title: 'Inquiry \u2192 member conversions',
    dataKey: 'inquiry_converted_accounts'
  },
  'dropin-pass-plan': {
    title: 'Drop-in \u2192 plan/pass purchases',
    dataKey: 'dropin_bought_pass_or_plan_accounts'
  },
  'dropin-repeat-booking': {
    title: 'Drop-in \u2192 return bookings',
    dataKey: 'dropin_made_second_booking_accounts'
  }
}

const urlParams = new URLSearchParams(window.location.search)
const conversionType = urlParams.get('conversionType') || 'dropin-member'
const config = CONVERSION_TYPES[conversionType] || CONVERSION_TYPES['dropin-member']

const panelTitle = config.title

const { data, loading, error, cleanup } = useDashboardKPIsData({
  variables: computed(() => ({ dateRange: 'last_30_days' })),
  priority: 1
})

const accounts = computed(() => {
  const items = data.value?.dashboardKPIs?.[config.dataKey]?.data ?? []
  return items.map((item) => ({
    account_id: item.account_id,
    name: item.name || 'Unknown',
    avatar: getAvatarUrl(item.avatar?.url, item.name || item.account_id)
  }))
})

const PAGE_SIZE = 20
const visibleCount = ref(PAGE_SIZE)
const visibleAccounts = computed(() => accounts.value.slice(0, visibleCount.value))
const hasMore = computed(() => visibleCount.value < accounts.value.length)
const loadMore = () => { visibleCount.value += PAGE_SIZE }

const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  return words.length >= 2 ? (words[0][0] + words[words.length - 1][0]).toUpperCase() : name.charAt(0).toUpperCase()
}

const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search)
  let clientId = params.get('client_id') || params.get('clientId') || ''
  let canvasUUID = params.get('canvasUUID') || params.get('canvas_uuid') || params.get('canvasUuid') || ''
  if (document.referrer) {
    try {
      const referrerParams = new URLSearchParams(new URL(document.referrer).search)
      if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
      if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
    } catch (_e) { /* ignore */ }
  }
  return { client_id: clientId, canvasUUID }
}

const { guard } = useTrialModeGuard()
const handleAccountClickGuarded = guard((account) => {
  if (!account?.account_id) return
  const params = getUrlParams()
  const message = {
    command: 'ViewDetails',
    payload: {
      account_id: String(account.account_id),
      canvasUUID: params.canvasUUID || 'xxx'
    }
  }
  try {
    window.parent.postMessage(message, '*')
    if (window.top && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
  } catch (e) {
    console.error('Failed to open account panel:', e)
  }
})

onUnmounted(() => { cleanup() })
</script>

<style scoped>
.view-all-panel {
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  flex-shrink: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
}

.panel-title {
  flex: 1;
  font-size: 24px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.panel-loading,
.panel-empty {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}

.panel-error {
  font-size: 14px;
  color: var(--color-red);
}

.panel-table {
  width: 100%;
  border-collapse: collapse;
}

.panel-table th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.panel-table tbody tr {
  height: 48px;
}

.panel-table td {
  padding: 0 12px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.panel-table tbody tr:last-child td {
  border-bottom: none;
}

.panel-table tbody tr.clickable {
  cursor: pointer;
}

.panel-table tbody tr.clickable:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.user-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.user-image {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.user-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.user-name {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.load-more-btn {
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: white;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.load-more-btn:hover {
  background: #f3f4f6;
}
</style>
